// AuthProvider — the React context that owns "who is logged in".
//
// Two modes:
//   1. Cognito mode (production): wraps react-oidc-context's AuthProvider,
//      configured with PKCE against our User Pool.
//   2. Dev mode (Phase 1, before Cognito exists): hardcoded username, no
//      tokens. Lets us boot the app and click around tools with a fake
//      identity. Toggle via VITE_AUTH_DEV_MODE in .env.local.
//
// In both modes the consumer-facing API is identical:
//   const { username, idToken, isAuthenticated, signIn, signOut } = useAuth();

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';

import { COGNITO, AUTH_DEV_MODE, AUTH_DEV_USERNAME, AUTH_DEV_IS_ADMIN } from '../config.js';
import { authStore } from './authStore.js';
import { setHarmonyUsername, deriveUsernameFromEmail } from '../lib/harmonyShim.js';

// ---------------------------------------------------------------------------
// Internal context
// ---------------------------------------------------------------------------
const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

// ---------------------------------------------------------------------------
// Cognito (real) mode
// ---------------------------------------------------------------------------
function CognitoAuthBridge({ children }) {
  // react-oidc-context exposes the OIDC user. We translate to our shape.
  const oidc = useOidcAuth();

  const idToken = oidc.user?.id_token || null;
  const profile = oidc.user?.profile || {};
  const email = profile.email || profile['cognito:username'] || '';
  const username = useMemo(() => deriveUsernameFromEmail(email), [email]);
  // Cognito puts group memberships on the `cognito:groups` claim. Tokens
  // emitted before a user is added to a group won't have it — they'll
  // pick it up on next refresh / re-login.
  const groups = useMemo(() => {
    const raw = profile['cognito:groups'];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.length) return [raw];
    return [];
  }, [profile]);
  const isAdmin = groups.includes('admins');
  // Internal-but-not-admin users get view-only access to certain surfaces
  // (e.g. the AI Chat knowledge base admin page — see
  // .kiro/steering/ai-chat-tracker.md Part 5). Broader than isAdmin,
  // narrower than "any authenticated user".
  const isInternal = isAdmin || groups.includes('amazon-internal');
  const isAuthenticated = oidc.isAuthenticated && !oidc.isLoading;

  // Populate authStore + harmonyShim DURING RENDER, not in an effect.
  //
  // Why: ResultsSection (and other tool components) call apiFetch from their
  // own useEffect. If we wait for our useEffect to run first, there's a race
  // where children's effects can fire before ours — they read an empty
  // authStore, send a request with no Bearer header, and get 401.
  //
  // Doing this assignment during render is safe because:
  //   1. authStore is a module-scoped plain object, not React state.
  //      No setState, no re-render triggered.
  //   2. ProtectedRoute won't render children until isAuthenticated is true,
  //      so by the time tool effects run, authStore is already populated.
  //   3. Idempotent — assigning the same token repeatedly is a no-op.
  if (isAuthenticated && username) {
    authStore.setSession({ idToken, username });
    setHarmonyUsername(username);
  }

  // Still need an effect to CLEAR the store on sign-out (mirrors the cleanup
  // path of the original logic).
  useEffect(() => {
    if (!isAuthenticated) {
      authStore.clearSession();
    }
  }, [isAuthenticated]);

  const value = useMemo(
    () => ({
      mode: 'cognito',
      isLoading: oidc.isLoading,
      isAuthenticated,
      username,
      email,
      idToken,
      groups,
      isAdmin,
      isInternal,
      error: oidc.error,
      signIn: () => oidc.signinRedirect(),
      signOut: () => {
        // Cognito's logout endpoint clears the session, then redirects back.
        oidc.removeUser();
        authStore.clearSession();
        const logoutUrl = `${COGNITO.domain}/logout?` + new URLSearchParams({
          client_id: COGNITO.clientId,
          logout_uri: COGNITO.logoutUri,
        }).toString();
        window.location.assign(logoutUrl);
      },
    }),
    [oidc, isAuthenticated, username, email, idToken, groups, isAdmin, isInternal]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Dev mode (no Cognito)
// ---------------------------------------------------------------------------
function DevAuthBridge({ children }) {
  const username = AUTH_DEV_USERNAME;
  const [_, force] = useState(0);

  useEffect(() => {
    authStore.setSession({ idToken: null, username });
    setHarmonyUsername(username);
    // Re-render once on mount so consumers see the populated state on
    // first read.
    force((n) => n + 1);
  }, [username]);

  const value = useMemo(
    () => ({
      mode: 'dev',
      isLoading: false,
      isAuthenticated: true,
      username,
      email: `${username}@dev.local`,
      idToken: null,
      groups: AUTH_DEV_IS_ADMIN ? ['admins'] : [],
      isAdmin: AUTH_DEV_IS_ADMIN,
      isInternal: AUTH_DEV_IS_ADMIN,
      error: null,
      signIn: () => {
        // No-op in dev mode — already "signed in" as the hardcoded user.
        console.warn('[auth] signIn() called in dev mode — no-op');
      },
      signOut: () => {
        console.warn('[auth] signOut() called in dev mode — no-op');
      },
    }),
    [username]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Top-level wrapper — picks the right mode based on env config
// ---------------------------------------------------------------------------
export function AuthProvider({ children }) {
  if (AUTH_DEV_MODE) {
    return <DevAuthBridge>{children}</DevAuthBridge>;
  }

  // Cognito OIDC settings. PKCE flow — no client secret needed for SPAs.
  // Tokens stored in localStorage so refresh survives page reloads. (We can
  // tighten to sessionStorage later if security review prefers — both are
  // exposed to XSS, so the real defense is CSP + careful dependency hygiene.)
  const oidcConfig = {
    authority: COGNITO.authority,
    client_id: COGNITO.clientId,
    redirect_uri: COGNITO.redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    automaticSilentRenew: true,
    onSigninCallback: () => {
      // After redirect back from Cognito, strip the `?code=...` from the URL
      // so refreshing the page doesn't re-trigger token exchange.
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  };

  return (
    <OidcAuthProvider {...oidcConfig}>
      <CognitoAuthBridge>{children}</CognitoAuthBridge>
    </OidcAuthProvider>
  );
}
